/**
 * The `crowProp` bird in an SDK scene: perched in the scene, flying the
 * opening tour with the camera behind it, and taking off when its POI is picked.
 */
import type { DatumEngine } from '@datum-sdk/engine';
import type { CrowPropConfig, EnvironmentConfig } from '../../sceneConfig';
import { createCrowBirdPoi } from './crowBirdPoi';
import { createCrowOpeningTour } from './crowOpeningTour';
import { watchCrowPick } from './crowPickFlight';
import { exposeCrowTourDevHooks } from './crowTourDevHooks';
import { createSdkCrowBird } from './sdkCrowBird';

export interface SdkCrow {
  /**
   * The scene-opening tour, once the bird is in. `onDone` fires when the camera
   * is handed back — or as soon as there is nothing to fly: no tour, or a bird
   * that failed to load (the scene must still finish loading, as in legacy).
   */
  playOpeningTour(onDone: () => void): void;
  dispose(): void;
}

export const mountSdkCrow = (
  engine: DatumEngine,
  crowConfig: CrowPropConfig,
  environment: EnvironmentConfig,
  sceneId: string,
  /** `window.__crowTour` for authoring the tour (`?dev`). */
  devHooks = false,
): SdkCrow => {
  const bird = createSdkCrowBird(engine, crowConfig, environment, sceneId);
  const birdPoi = createCrowBirdPoi(engine, crowConfig.sightId);
  const tour = crowConfig.tour
    ? createCrowOpeningTour(engine, crowConfig.position, crowConfig.tour, bird, birdPoi)
    : null;
  const stopPickWatch = watchCrowPick(
    engine,
    crowConfig.sightId,
    () => bird.isFlying() || (tour?.isChasing() ?? false),
    bird.flyRandomLoop,
  );
  const removeDevHooks =
    devHooks && crowConfig.tour && tour
      ? exposeCrowTourDevHooks(engine, crowConfig.tour, tour)
      : null;
  let disposed = false;
  return {
    playOpeningTour: (onDone) => {
      void bird.loaded.then((inScene) => {
        if (disposed) return;
        if (inScene && tour) tour.play(onDone);
        else onDone();
      });
    },
    dispose: () => {
      disposed = true;
      removeDevHooks?.();
      stopPickWatch();
      tour?.dispose();
      bird.dispose();
      // `$suppressedSightIds` outlives the scene: torn down mid-flight, the
      // bird's chip would stay missing until the store's own reset.
      birdPoi.restore();
    },
  };
};
